import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RemoveIcon from "@mui/icons-material/Remove";
import { Link } from "react-router-dom";
import { getActiveQuoteLines, normalizeQuoteLines } from "../utils/shopApi";
import { NAV } from "../Navbar/navbarTheme";
import { AUTH_PRIMARY } from "../Pages/authStyles";

const PRIMARY = AUTH_PRIMARY;
const LINE = NAV.line;
const STORAGE_BASE = "https://api.uktomars.co.uk/storage";

const itemCardSx = {
  borderRadius: 3,
  border: `1px solid ${LINE}`,
  boxShadow: "0 12px 40px rgba(15, 40, 71, 0.08)",
  bgcolor: NAV.surface,
  overflow: "hidden",
};

const qtyButtonSx = {
  borderRadius: 0,
  width: 36,
  height: 36,
  color: PRIMARY,
  "&:hover": { bgcolor: NAV.navyMuted },
};

function getLineImage(line) {
  const product = line?.product;
  if (product?.image_url) return product.image_url;
  if (typeof product?.image === "string" && product.image.startsWith("http")) {
    return product.image;
  }
  if (typeof product?.image === "string" && product.image) {
    return `${STORAGE_BASE}/${product.image}`;
  }
  return "";
}

function QuoteQuantityControls({
  quantity,
  disabled,
  onDecrease,
  onIncrease,
  onRemove,
  compact = false,
}) {
  if (compact) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 1,
        }}
      >
        <IconButton
          size="small"
          aria-label="Decrease quantity"
          disabled={disabled}
          onClick={onDecrease}
          sx={{
            border: `1px solid ${LINE}`,
            borderRadius: 1,
            p: 0.35,
            bgcolor: NAV.surfaceSubtle,
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 700,
            minWidth: 24,
            textAlign: "center",
            color: NAV.navy,
          }}
        >
          {quantity}
        </Typography>
        <IconButton
          size="small"
          aria-label="Increase quantity"
          disabled={disabled}
          onClick={onIncrease}
          sx={{
            border: `1px solid ${LINE}`,
            borderRadius: 1,
            p: 0.35,
            bgcolor: NAV.surfaceSubtle,
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Remove item"
          disabled={disabled}
          onClick={onRemove}
          sx={{ ml: "auto", color: NAV.inkMuted }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          border: `1px solid ${LINE}`,
          borderRadius: 999,
          bgcolor: NAV.surfaceSubtle,
          overflow: "hidden",
        }}
      >
        <IconButton
          size="small"
          aria-label="Decrease quantity"
          disabled={disabled}
          onClick={onDecrease}
          sx={qtyButtonSx}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Typography
          sx={{
            fontWeight: 800,
            minWidth: 32,
            textAlign: "center",
            color: PRIMARY,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {quantity}
        </Typography>
        <IconButton
          size="small"
          aria-label="Increase quantity"
          disabled={disabled}
          onClick={onIncrease}
          sx={qtyButtonSx}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
      <IconButton
        size="small"
        aria-label="Remove item"
        disabled={disabled}
        onClick={onRemove}
        sx={{
          color: NAV.inkMuted,
          border: `1px solid ${LINE}`,
          borderRadius: 999,
          "&:hover": {
            color: "#e31837",
            bgcolor: "rgba(227, 24, 55, 0.06)",
            borderColor: "rgba(227, 24, 55, 0.25)",
          },
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function PendingQuoteLineItem({
  line,
  quote,
  variant,
  disabled,
  onDecrease,
  onIncrease,
  onRemove,
}) {
  const imageUrl = getLineImage(line);
  const quantity = Number(line?.quantity) || 0;

  if (variant === "compact") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          py: 1.5,
          px: 1.25,
          mb: 1.25,
          borderRadius: 2,
          bgcolor: NAV.surface,
          border: `1px solid ${LINE}`,
          boxShadow: "0 2px 8px rgba(15, 40, 71, 0.06)",
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 1.25,
            border: `1px solid ${LINE}`,
            bgcolor: NAV.surfaceSubtle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={line.product_name || "Product"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Inventory2OutlinedIcon
              sx={{ color: NAV.inkMuted, fontSize: 24 }}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.8125rem",
              color: NAV.ink,
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {line.product_name || "Unnamed product"}
          </Typography>
          <QuoteQuantityControls
            compact
            quantity={quantity}
            disabled={disabled}
            onDecrease={onDecrease}
            onIncrease={onIncrease}
            onRemove={onRemove}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Card elevation={0} sx={itemCardSx}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
        >
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              border: `1px solid ${LINE}`,
              bgcolor: NAV.surfaceSubtle,
              flexShrink: 0,
              alignSelf: { xs: "center", sm: "flex-start" },
              width: { xs: 140, sm: 108 },
              height: { xs: 140, sm: 108 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={line.product_name || "Product"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <Inventory2OutlinedIcon
                sx={{ color: NAV.inkMuted, fontSize: 40 }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", sm: "1.0625rem" },
                color: PRIMARY,
                lineHeight: 1.35,
                mb: 1.5,
              }}
            >
              {line.product_name || "Unnamed product"}
            </Typography>
            <Box
              sx={{
                pt: 1.5,
                borderTop: `1px solid ${LINE}`,
              }}
            >
              <QuoteQuantityControls
                quantity={quantity}
                disabled={disabled}
                onDecrease={onDecrease}
                onIncrease={onIncrease}
                onRemove={onRemove}
              />
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function PendingQuoteItems({
  quotes = [],
  variant = "card",
  loading = false,
  error = "",
  updatingKey = null,
  onUpdateQuantity,
  onRemoveLine,
  showHeader = true,
}) {
  if (loading && quotes.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={28} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!quotes.length) {
    return null;
  }

  const visibleQuotes = quotes
    .map((quote) => normalizeQuoteLines(quote))
    .filter((quote) => getActiveQuoteLines(quote.lines).length > 0);

  if (!visibleQuotes.length) {
    return null;
  }

  const singleQuote =
    visibleQuotes.length === 1 ? visibleQuotes[0] : null;
  const singleQuoteLabel = singleQuote
    ? singleQuote.reference || `Quote #${singleQuote.id}`
    : null;

  return (
    <Box sx={{ mb: variant === "compact" ? 2 : 3 }}>
      {showHeader ? (
        <Box
          sx={{
            mb: variant === "compact" ? 1.5 : 2,
            p: variant === "compact" ? 1.25 : 1.5,
            borderRadius: 2,
            bgcolor: "rgba(245, 158, 11, 0.08)",
            border: "1px solid rgba(245, 158, 11, 0.22)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
          >
            <Box sx={{ minWidth: 0 }}>
              {singleQuoteLabel && variant === "compact" ? (
                <Typography
                  component={Link}
                  to={`/QuoteHistory/${singleQuote.id}`}
                  state={{ quote: singleQuote, listPath: "/QuoteHistory" }}
                  sx={{
                    display: "inline-block",
                    fontWeight: 800,
                    color: PRIMARY,
                    fontSize: "0.875rem",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {singleQuoteLabel}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: PRIMARY,
                    fontSize: variant === "compact" ? "0.875rem" : "1rem",
                  }}
                >
                  Pending quote request
                </Typography>
              )}
              {!(singleQuoteLabel && variant === "compact") ? (
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: NAV.inkMuted,
                    mt: 0.35,
                    lineHeight: 1.5,
                  }}
                >
                  You can update quantities or remove products while this request
                  is pending.
                </Typography>
              ) : (
                <Typography
                  component={Link}
                  to={`/QuoteHistory/${singleQuote.id}`}
                  state={{ quote: singleQuote, listPath: "/QuoteHistory" }}
                  sx={{
                    display: "inline-block",
                    mt: 0.35,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: NAV.inkMuted,
                    textDecoration: "none",
                    "&:hover": {
                      color: PRIMARY,
                      textDecoration: "underline",
                    },
                  }}
                >
                  View quote details
                </Typography>
              )}
            </Box>
            <Chip
              label="Editable"
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: "rgba(245, 158, 11, 0.12)",
                color: "#b45309",
              }}
            />
          </Stack>
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={variant === "compact" ? 0 : 2}>
        {visibleQuotes.map((quote) => {
          const lines = getActiveQuoteLines(quote.lines);
          return (
            <Box key={quote.id}>
              {visibleQuotes.length > 1 ? (
                <Typography
                  component={Link}
                  to={`/QuoteHistory/${quote.id}`}
                  state={{ quote, listPath: "/QuoteHistory" }}
                  sx={{
                    display: "block",
                    mb: 1,
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                    color: PRIMARY,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {quote.reference || `Quote #${quote.id}`}
                </Typography>
              ) : null}
              {lines.map((line) => {
                const lineKey = `${quote.id}:${line.id}`;
                const disabled =
                  updatingKey === `${quote.id}:${line.id}:qty` ||
                  updatingKey === `${quote.id}:${line.id}:remove`;
                return (
                  <PendingQuoteLineItem
                    key={lineKey}
                    line={line}
                    quote={quote}
                    variant={variant}
                    disabled={disabled}
                    onDecrease={() => onUpdateQuantity?.(quote, line.id, -1)}
                    onIncrease={() => onUpdateQuantity?.(quote, line.id, 1)}
                    onRemove={() => onRemoveLine?.(quote, line.id)}
                  />
                );
              })}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default PendingQuoteItems;
